/* eslint-disable prefer-destructuring */
/* eslint-disable no-restricted-syntax */
import {
	CircularProgress,
	Icon,
	List,
	ListItemButton,
	ListItemText,
	Avatar,
	Chip
} from "@mui/material";
import Grid from "@mui/material/Grid";
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { SHOPIFY_RESOURCE } from "constants";
import { checkIfSourcesExist } from "hooks/sources";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SourceActions from "redux-react/actions/sourceActions";
import FaiqActions from "redux-react/actions/faiqActions";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import { display } from "redux-react/reducers/snackBarReducer";
import { replaceInTextHbr } from "helpers/utilities";

const HBR_TEMPLATE = `
{{#if title}}
<h1>{{title}}</h1>
{{/if}}

{{#if vendor}}
<p><strong>Marque :</strong> {{vendor}}</p>
{{/if}}

{{#if product_type}}
<p><strong>Type :</strong> {{product_type}}</p>
{{/if}}

{{#if body_html}}
{{{body_html}}}
{{/if}}

{{#if variants}}
<h2>Prix et disponibilités</h2>
<ul>
  {{#each variants}}
    <li>
      {{#if title}}
        <strong>{{title}}</strong> :
      {{/if}}

      {{#if price}}
        {{price}} €
      {{/if}}

      {{#if compare_at_price}}
        (au lieu de {{compare_at_price}} €)
      {{/if}}

      {{#if inventory_quantity}}
        — Stock : {{inventory_quantity}}
      {{/if}}
    </li>
  {{/each}}
</ul>
{{/if}}

{{#if options}}
<h2>Options</h2>
  {{#each options}}
    {{#if name}}
      <p><strong>{{name}}</strong></p>
    {{/if}}
    {{#if values}}
      <ul>
        {{#each values}}
          <li>{{this}}</li>
        {{/each}}
      </ul>
    {{/if}}
  {{/each}}
{{/if}}
`;

/**
 * Grid item component
 * @param {*} param0
 * @returns {JSX.Element} - Grid item
 */
const GridItem = ({ icon, text, onClick, ...rest }) => {
	return (
		<Grid
			item
			xs={6}
			sm={4}
			md={3}
			lg={2}
			style={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				alignItems: "center"
			}}
			sx={{
				"&:hover": {
					backgroundColor: "grey.100",
					cursor: "pointer"
				}
			}}
			onClick={onClick}
			{...rest}
		>
			<MDBox
				className="integrationBox"
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="space-between"
				borderRadius="lg"
				p={2}
				sx={{
					height: "100%",
					width: "100%"
				}}
				onClick={() => {}}
			>
				<MDBox>
					<Icon fontSize="large">{icon}</Icon>
				</MDBox>
				<MDTypography variant="body1" fontSize="small">
					{text}
				</MDTypography>
			</MDBox>
		</Grid>
	);
};

/**
 * Shopify selection component
 * @param {*} param0
 * @returns  {JSX.Element} ShopifySelection
 */
const ShopifySelection = ({ onFilesSelection }) => {
	const dispatch = useDispatch();

	const profile = useSelector(selectCurrentProfile);

	const [channelAssistantID, setChannelAssistantID] = useState(null);
	const [loadingIntegrations, setLoadingIntegrations] = useState(true);
	const [shopifyChannels, setShopifyChannels] = useState([]);
	const [selectedChannel, setSelectedChannel] = useState(null);
	const [loadingItems, setLoadingItems] = useState(false);

	/**
	 * Convert shopify result into "file" format
	 * @param {object} result - Shopify result
	 * @param {string} domain - Shopify domain
	 * @param {string} resource - Shopify resource
	 */
	const mapResult = async (result, domain, resource) => {
		const files = [];
		for (let item of result) {
			const url = `${domain}${resource}/${item.handle}`;

			let content = "";
			try {
				content = replaceInTextHbr(HBR_TEMPLATE, item);
			} catch {
				content = item.body_html || "";
			}

			let file = {
				url,
				name: item.title,
				extra: {
					apiResult: item,
					rawText: content
				},
				metadatas: {
					url
				}
			};
			files.push(file);
		}

		const filesResult = await checkIfSourcesExist(dispatch, files);
		setLoadingItems(false);
		onFilesSelection(filesResult);
	};

	/**
	 * Get shopify items
	 * @param {string} resource - Shopify resource
	 */
	const getShopifyItems = async resource => {
		if (lod_.isNil(selectedChannel)) {
			return;
		}
		setLoadingItems(true);
		dispatch(
			SourceActions.getShopifyResourceForChannel(
				resource,
				channelAssistantID,
				selectedChannel.code,
				({ result, domain }) => {
					mapResult(result, domain, resource);
				},
				() => {
					setLoadingItems(false);
					dispatch(
						display({
							type: "error",
							message: t("SOURCES.IMPORT.error")
						})
					);
				}
			)
		);
	};

	/**
	 * Load config
	 */
	function getPossibleIntegrations() {
		// Result function
		const onSuccess = res => {
			// If config is not nil
			if (!lod_.isNil(res?.config)) {
				const config = res.config;

				// Get the channel assistant ID (used to link faiq app with cockpit app)
				if (config.channelAssistantID) {
					setChannelAssistantID(config.channelAssistantID);

					// Get the possible integrations
					dispatch(
						SourceActions.getIntegrationChannels(
							{
								assistantID: config.channelAssistantID,
								channels: ["SHFY"]
							},
							res => {
								if (!lod_.isNil(res)) {
									const sortedChannels = [...res].sort((a, b) => {
										// Sort by active (true first), then by name (alphabetically)
										if (a.active === b.active) {
											return (a.name || "").localeCompare(b.name || "");
										}
										return a.active ? -1 : 1;
									});
									setShopifyChannels(sortedChannels);
									// Select first active channel by default
									const firstActiveChannel = sortedChannels.find(channel => channel.active);
									if (firstActiveChannel) {
										setSelectedChannel(firstActiveChannel);
									}
								}
								setLoadingIntegrations(false);
							},
							() => {
								setLoadingIntegrations(false);
							}
						)
					);
				} else {
					setLoadingIntegrations(false);
				}
			} else {
				setLoadingIntegrations(false);
			}
		};

		// Get the config
		setLoadingIntegrations(true);
		dispatch(
			FaiqActions.getFaiqConfig(profile.assistantID, onSuccess, () => {
				setLoadingIntegrations(false);
			})
		);
	}

	/**
	 * Load possible integrations on component mount
	 */
	useEffect(() => {
		getPossibleIntegrations();
	}, []);

	return (
		<MDBox mt={5} display="flex" flex="1" flexDirection="row" gap={3}>
			{/* Left sidebar - Channels list */}
			<MDBox
				sx={{
					width: "280px",
					minWidth: "280px",
					borderRight: "1px solid",
					borderColor: "grey.300",
					pr: 2,
					overflowY: "auto",
					display: "flex",
					flexDirection: "column"
				}}
			>
				<MDTypography variant="h6" mb={2}>
					{t("SOURCES.availableChannels", "Canaux disponibles")}
				</MDTypography>

				{loadingIntegrations === true && (
					<MDBox
						display="flex"
						justifyContent="center"
						alignItems="center"
						sx={{ height: "200px" }}
					>
						<CircularProgress color="info" size={30} />
					</MDBox>
				)}

				{loadingIntegrations === false && shopifyChannels.length === 0 && (
					<MDBox
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						sx={{ height: "200px", textAlign: "center" }}
					>
						<Icon fontSize="large" color="disabled">
							store
						</Icon>
						<MDTypography variant="body2" color="text.secondary" mt={1}>
							{t("SOURCES.SHOPIFY.noChannels", "Aucun canal Shopify configuré")}
						</MDTypography>
					</MDBox>
				)}

				{loadingIntegrations === false && shopifyChannels.length > 0 && (
					<List sx={{ overflowY: "auto" }}>
						{shopifyChannels.map(channel => (
							<ListItemButton
								key={channel.code}
								selected={selectedChannel?.code === channel.code}
								disabled={!channel.active}
								onClick={() => {
									setSelectedChannel(channel);
									setLoadingItems(false);
								}}
								sx={{
									borderRadius: "8px",
									mb: 1,
									"&.Mui-selected": {
										backgroundColor: "info.main",
										color: "white"
									},
									"&.Mui-selected:hover": {
										backgroundColor: "info.main"
									},
									"&.Mui-disabled": {
										opacity: 0.5
									}
								}}
							>
								<Avatar
									src={channel.logoURL}
									alt={channel.name}
									sx={{ width: 32, height: 32, mr: 2 }}
								/>
								<ListItemText
									primary={channel.name}
									secondary={channel.description}
									primaryTypographyProps={{
										color: selectedChannel?.code === channel.code ? "white" : "text.primary",
										fontWeight: selectedChannel?.code === channel.code ? "bold" : "normal",
										fontSize: "medium"
									}}
									secondaryTypographyProps={{
										color: selectedChannel?.code === channel.code ? "white" : "text.secondary",
										fontSize: "small"
									}}
								/>
								{!channel.active && (
									<Chip
										label={t("SOURCES.SHOPIFY.inactive", "Inactif")}
										size="small"
										color="default"
										sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
									/>
								)}
							</ListItemButton>
						))}
					</List>
				)}
			</MDBox>

			{/* Right content - Resource selection */}
			<MDBox sx={{ flex: 1 }}>
				{loadingItems === true && (
					<MDBox display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
						<CircularProgress color="info" size={50} />
					</MDBox>
				)}

				{loadingItems === false && loadingIntegrations === false && lod_.isNil(selectedChannel) && (
					<MDBox
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						sx={{ height: "300px", textAlign: "center" }}
					>
						<Icon fontSize="large" color="disabled">
							touch_app
						</Icon>
						<MDTypography variant="body2" color="text.secondary" mt={1}>
							{t("SOURCES.SHOPIFY.selectChannel", "Veuillez sélectionner un canal")}
						</MDTypography>
					</MDBox>
				)}

				{loadingItems === false &&
					loadingIntegrations === false &&
					!lod_.isNil(selectedChannel) && (
						<>
							<MDTypography variant="h4">{`${t("SOURCES.SHOPIFY.insertDatasFromShopifyTitle")}: ${selectedChannel?.name}`}</MDTypography>
							<MDTypography variant="subtitle2">
								{t("SOURCES.SHOPIFY.insertDatasFromShopifySubtitle")}
							</MDTypography>
							<Grid container sx={{ mt: 5 }}>
								<GridItem
									icon="description"
									text={t("SOURCES.SHOPIFY.shopifyPages")}
									onClick={() => getShopifyItems(SHOPIFY_RESOURCE.PAGES)}
								/>
								<GridItem
									icon="shopping_cart"
									text={t("SOURCES.SHOPIFY.shopifyProducts")}
									onClick={() => getShopifyItems(SHOPIFY_RESOURCE.PRODUCTS)}
								/>
							</Grid>
						</>
					)}
			</MDBox>
		</MDBox>
	);
};

export default ShopifySelection;
